import { Close } from '@tunasong/icons'
import type { SortOptions } from '@tunasong/models'
import type { Entity, EntityType } from '@tunasong/schemas'
import cn from 'classnames'
import type { FC, HTMLAttributes, KeyboardEvent } from 'react'
import { useEffect, useId, useState } from 'react'
import { filterByText } from './list-text-filter.js'
import { SortMenu } from './sort-menu.js'
import styles from './sort-filter-bar.module.css'
import { TypeFilterChips } from './type-filter-chips.js'

/** The list's text filter (GH #2519 A), from `useListTextFilter` */
export interface SortFilterBarTextFilter {
  /** What the field shows */
  text: string
  /** What the list is filtered by: the text, debounced */
  query: string
  onText(text: string): void
}

export interface SortFilterBarProps extends HTMLAttributes<HTMLDivElement> {
  /** All entities in the list, before filtering. Used for the chips, their counts and the text filter. */
  entities: Pick<Entity, 'type' | 'name' | 'tags'>[]
  filter: EntityType[]
  sortOptions: SortOptions
  /** Leave the sort menu out, e.g. when the host shows its own `SortMenu` on a heading row. @default false */
  hideSort?: boolean
  /** The text filter; absent leaves the field out */
  textFilter?: SortFilterBarTextFilter
  /** Read-only grids (GH #2519 A): the text field alone, with no chips and no sort. @default false */
  fieldOnly?: boolean
  onFilter(filter: EntityType[]): void
  onSort(options: SortOptions): void
}

/** Joni's spec, A: the count is announced once it settles, not on every key */
const announceDelayMs = 500

/** The shown count is announced to a screen reader once it stops changing */
const useSettled = (text: string) => {
  const [settled, setSettled] = useState('')
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(text), announceDelayMs)
    return () => window.clearTimeout(timer)
  }, [text])
  return settled
}

/**
 * Toolbar above an entity list: the text filter (GH #2519 A), type filter chips and the sort menu, with
 * `Showing n of m · Clear filter` while either filter is active.
 */
export const SortFilterBar: FC<SortFilterBarProps> = props => {
  const {
    entities,
    filter,
    onFilter,
    sortOptions,
    onSort,
    hideSort = false,
    textFilter,
    fieldOnly = false,
    className,
    ...restProps
  } = props
  const fieldId = useId()

  const query = textFilter?.query ?? ''
  const byType = filter.length > 0 ? entities.filter(e => filter.includes(e.type)) : entities
  const shown = filterByText(byType, query).length
  const isFiltered = filter.length > 0 || query.trim() !== ''
  const summary = `Showing ${shown} of ${entities.length}`
  const announced = useSettled(isFiltered ? summary : '')

  const clearAll = () => {
    onFilter([])
    textFilter?.onText('')
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && textFilter?.text) {
      event.preventDefault()
      textFilter.onText('')
    }
  }

  const field = textFilter ? (
    <div className={styles.field}>
      <label htmlFor={fieldId} className={styles.label}>
        Filter this list
      </label>
      <input
        id={fieldId}
        type="search"
        className={styles.input}
        placeholder="Filter by name or tag"
        value={textFilter.text}
        onChange={event => textFilter.onText(event.target.value)}
        onKeyDown={handleKeyDown}
      />
      {textFilter.text ? (
        <button
          type="button"
          className={styles.clearText}
          aria-label="Clear filter text"
          onClick={() => textFilter.onText('')}
        >
          <Close size={16} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  ) : null

  return (
    <div className={cn(styles.root, className)} {...restProps}>
      <div className={cn(styles.toolbar, fieldOnly && styles.fieldOnly)}>
        {field}
        {fieldOnly ? null : (
          <>
            <TypeFilterChips className={styles.chips} entities={entities} value={filter} onChange={onFilter} />
            {hideSort ? null : <SortMenu value={sortOptions} onChange={onSort} reorderBlocked={query.trim() !== ''} />}
          </>
        )}
      </div>
      {isFiltered && shown === 0 && query.trim() !== '' ? (
        <div className={styles.empty}>
          <span>{`No items match "${query.trim()}".`}</span>
          <button type="button" className={styles.clear} onClick={clearAll}>
            Clear filter
          </button>
        </div>
      ) : isFiltered ? (
        <div className={styles.summary}>
          <span>{summary}</span>
          <button type="button" className={styles.clear} onClick={clearAll}>
            Clear filter
          </button>
        </div>
      ) : null}
      <span role="status" aria-live="polite" className={styles.visuallyHidden}>
        {announced}
      </span>
    </div>
  )
}

export default SortFilterBar
